/*
 * Stock movements — the enum, its presentation, and the two derivations the dialogs need.
 *
 * Shaped like features/cmms/toolServiceStatus.js: the numeric enum, helpers that take an
 * `intl`, and pure functions beside them so the rules can be tested without rendering
 * anything. Labels come from cmmsMessages, never the other way round — cmmsMessages must not
 * import an enums module, or the cycle documented at the top of that file comes back as a TDZ
 * error at runtime.
 */
import { getStockMovementTypeLabel } from "../cmms/cmmsMessages";
import messages from "./messages";

// Mirrors Models/Cmms/StockMovementType.cs, holes and all.
//
// 2 (Issue) and 4 (Transfer) are reservations, not omissions. Issue *is* MaterialUsage: the
// consumption row is the outgoing movement, so there is no second write to forget — which is
// exactly what the cmms/poc branch forgot, leaving its stock and its consumption disagreeing
// from the first booking. Transfer needs a second location and the register has one field.
export const StockMovementType = {
  Receipt: 1,
  Adjustment: 3,
  Return: 5,
  Stocktake: 6,
};

// Which way an adjustment goes. Only meaningful for Adjustment — see toSignedQuantity.
export const AdjustmentDirection = {
  Increase: "increase",
  Decrease: "decrease",
};

export { getStockMovementTypeLabel };

/*
 * An empty field is not a zero.
 *
 * Number("") is 0, which is the single most expensive coercion in this file: it would let a
 * blank count preview a difference of "-7" as though someone had counted nothing, and it would
 * let a blank quantity through as a movement of no size. Every numeric entry here goes through
 * this first.
 */
const isBlank = (value) => value === "" || value === null || value === undefined;

/*
 * The types a person can file from the movement form.
 *
 * Deliberately not every value of the enum. Stocktake is refused by
 * StockMovementService.CreateAsync — it has a route of its own that works the delta out
 * server-side — so offering it here would put a guaranteed 400 in a dropdown.
 */
const creatableTypes = [
  StockMovementType.Receipt,
  StockMovementType.Adjustment,
  StockMovementType.Return,
];

export const getStockMovementTypeOptions = (intl) =>
  creatableTypes.map((value) => ({
    id: value,
    value,
    title: getStockMovementTypeLabel(value, intl),
    label: getStockMovementTypeLabel(value, intl),
  }));

/*
 * What actually gets sent as `quantity`.
 *
 * The form asks for a magnitude and, for an adjustment, a direction. This is the one place the
 * two become the signed number the ledger stores, and it is a function rather than an
 * expression inside the dialog so the rule can be tested.
 *
 * The direction is inert for anything but an adjustment. That matters: the radio keeps its
 * value when someone switches the type back to Goods received, and without this a stale
 * "Decrease" would post a negative receipt — which the service rejects, but only after the
 * user has already been told their entry was fine.
 */
export const toSignedQuantity = (type, direction, magnitude) => {
  if (isBlank(magnitude)) return Number.NaN;

  const value = Math.abs(Number(magnitude));

  if (Number.isNaN(value)) return Number.NaN;

  return type === StockMovementType.Adjustment && direction === AdjustmentDirection.Decrease
    ? -value
    : value;
};

/*
 * Mirrors StockMovementService.ValidateMovement so the answer arrives before the round trip.
 *
 * It does not replace the server's check — a 400 is still rendered as it comes, with the
 * server's own words, which is how CMMS_SPARE_PART_IS_INACTIVE reaches the user as a sentence
 * about a retired part instead of a generic failure.
 *
 * Returns a field -> message map, empty when the form is fine, matching the `errors` shape the
 * other CMMS dialogs already use.
 */
export const validateStockMovementForm = (form, intl) => {
  const errors = {};
  const quantity = Number(form?.quantity);

  if (form?.quantity === "" || form?.quantity === null || form?.quantity === undefined
      || Number.isNaN(quantity) || quantity <= 0) {
    errors.quantity = intl.formatMessage(messages.validationQuantityPositive);
  }

  // A movement that changes the balance without saying why is what makes a balance impossible
  // to trust afterwards. Receipts and returns carry their own explanation in the type.
  if (form?.movementType === StockMovementType.Adjustment && !form?.reason?.trim()) {
    errors.reason = intl.formatMessage(messages.validationReasonRequired);
  }

  return errors;
};

/*
 * What the stocktake will write.
 *
 * The dialog shows this before anything is sent, and the number it shows is the number the
 * service computes — same subtraction, same order. Not sent: the request carries the counted
 * quantity, because a client that computes its own delta computes it against a balance it read
 * a while ago.
 */
export const computeStocktakeDelta = (countedQuantity, balance) => {
  if (isBlank(countedQuantity) || isBlank(balance)) return Number.NaN;

  const counted = Number(countedQuantity);
  const current = Number(balance);

  if (Number.isNaN(counted) || Number.isNaN(current)) return Number.NaN;

  return counted - current;
};

/*
 * Colour for a signed quantity in the ledger.
 *
 * Severity, not sentiment: a receipt is not "good news" and a consumption is not bad. The
 * pairing is the one the theme already gives a reader — something added, something taken away.
 */
export const getStockMovementQuantityColor = (quantity) => {
  const value = Number(quantity);
  if (!value) return "text.secondary";
  return value > 0 ? "success.main" : "error.main";
};
